import { AnimatePresence, motion } from "framer-motion";
import { Bot, EyeOff, WifiOff } from "lucide-react";
import { anchorRef, seatAnchor } from "../fxAnchors";
import { ACTIONS, ROLES } from "../gameData";
import { useHoldReveal } from "../useHoldReveal";
import type { Player } from "../types";

interface Props {
  players: Player[];
  meId: string;
  activePlayerId: string | null;
  targeting: boolean;
  onTarget: (playerId: string) => void;
}

/*
 * Coins as a gauge rather than a number. In VEIL a coin pile is really a
 * countdown to violence: 7 buys a Coup, and at 10 a Coup is compulsory. Twelve
 * is the true ceiling — you can only ever start a turn on 9 (10 forces the
 * Coup) and the biggest single gain is Duke's +3.
 *
 * The arc deliberately stops short of the top. That 105 degree notch is where
 * the NOW / NEXT chip sits, which used to collide with the avatar; the gap
 * turns an old overlap into the shape's negative space.
 */
const COIN_MAX = 12;
const COUP_COST = 7;
const COUP_FORCED = 10;

const RING_R = 29;
const RING_C = 36;
const GAP_DEG = 105;
const SWEEP_DEG = 360 - GAP_DEG;
const SLOT_DEG = SWEEP_DEG / COIN_MAX;
const TICK_DEG = SLOT_DEG * 0.73;
const START_DEG = -90 + GAP_DEG / 2;

function tickPath(index: number) {
  const from = START_DEG + index * SLOT_DEG;
  const to = from + TICK_DEG;
  const point = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return `${(RING_C + RING_R * Math.cos(rad)).toFixed(2)} ${(RING_C + RING_R * Math.sin(rad)).toFixed(2)}`;
  };
  return `M ${point(from)} A ${RING_R} ${RING_R} 0 0 1 ${point(to)}`;
}

const TICKS = Array.from({ length: COIN_MAX }, (_, index) => tickPath(index));

function CoinRing({ coins }: { coins: number }) {
  const filled = Math.max(0, Math.min(COIN_MAX, coins));
  const level = filled >= COUP_FORCED ? "forced" : filled >= COUP_COST ? "armed" : "calm";
  return (
    <svg
      className={`coin-ring coin-ring--${level}`}
      viewBox="0 0 72 72"
      aria-hidden="true"
      focusable="false"
    >
      {TICKS.map((d, index) => (
        <path
          key={d}
          d={d}
          className={index < filled ? "coin-ring__tick coin-ring__tick--on" : "coin-ring__tick"}
        />
      ))}
    </svg>
  );
}

export function PlayerRail({ players, meId, activePlayerId, targeting, onTarget }: Props) {
  const others = players.filter((player) => player.id !== meId);
  const alivePlayers = players.filter((player) => player.alive);
  const activeTurnIndex = alivePlayers.findIndex((player) => player.id === activePlayerId);
  // 450ms, cancelled by pointer drift, and the "that was a hold" flag is cleared
  // synchronously by the click handler — so a 300ms press still selects a target.
  const hold = useHoldReveal<string>();
  const heldPlayer = others.find((player) => player.id === hold.held) ?? null;
  const heldLostCards = heldPlayer?.influence.filter((card) => card.revealed && card.role) ?? [];
  // Per-person memory, newest first. VEIL keeps no table-wide log, but holding a
  // seat is meant to recall what THAT player has been claiming — that is reading
  // a person, which is the game, not reading a ledger.
  const heldPlays = heldPlayer ? [...(heldPlayer.recentPlays ?? [])].reverse() : [];

  return (
    <>
      <div
        className={[
          "player-rail",
          `player-rail--count-${others.length}`,
          targeting ? "player-rail--targeting" : ""
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={targeting ? "Choose a rival" : "Other players"}
      >
        {others.map((player, index) => {
          const lostCount = player.influence.filter((card) => card.revealed).length;
          const seatProgress = others.length <= 1 ? 0.5 : index / (others.length - 1);
          const arcSpan = [0, 0, 50, 68, 78, 80][Math.min(others.length, 5)] ?? 80;
          const arcDepth = [0, 0, 50, 75, 88, 94][Math.min(others.length, 5)] ?? 94;
          const normalizedSeatOffset = Math.abs(seatProgress - 0.5) * 2;
          const seatDepth = (1 - Math.cos((normalizedSeatOffset * Math.PI) / 2)) * arcDepth;
          const playerTurnIndex = alivePlayers.findIndex((alive) => alive.id === player.id);
          const turnsAway =
            activeTurnIndex >= 0 && playerTurnIndex >= 0
              ? (playerTurnIndex - activeTurnIndex + alivePlayers.length) % alivePlayers.length
              : null;
          const turnLabel = !player.alive
            ? "OUT"
            : turnsAway === 0
              ? "NOW"
              : turnsAway === 1
                ? "NEXT"
                : "";
          const turnDescription = !player.alive
            ? "Out of the game"
            : turnsAway === 0
              ? "Taking a turn now"
              : turnsAway === 1
                ? "Next in turn order"
                : turnsAway !== null
                  ? `${turnsAway} turns away`
                  : `Seat ${index + 1}`;
          const seatStyle = {
            "--seat-x": `${50 - arcSpan / 2 + seatProgress * arcSpan}%`,
            "--seat-y": `${22 + seatDepth}px`,
            "--seat-tilt": `${(seatProgress - 0.5) * 14}deg`,
            "--seat-layer": `${Math.round(8 - Math.abs(seatProgress - 0.5) * 4)}`
          } as React.CSSProperties;
          return (
            <motion.button
              type="button"
              layout
              key={player.id}
              style={seatStyle}
              className={[
                "player-pill",
                player.bot ? "player-pill--bot" : "",
                player.id === activePlayerId ? "player-pill--active" : "",
                !player.alive ? "player-pill--out" : "",
                targeting && player.alive ? "player-pill--targetable" : ""
              ]
                .filter(Boolean)
                .join(" ")}
              aria-label={`${player.name}${player.bot ? ", bot" : ""}. ${turnDescription}. ${lostCount} Influence lost. Hold to view their lost cards and recent moves${targeting && player.alive ? "; tap to target" : ""}.`}
              aria-current={player.id === activePlayerId ? "true" : undefined}
              {...hold.handlers(player.id)}
              onKeyDown={(event) => {
                if (event.key === " " || event.key === "Enter") hold.start(player.id);
              }}
              onKeyUp={(event) => {
                if (event.key === " " || event.key === "Enter") hold.end();
              }}
              onClick={hold.wrapClick(() => {
                if (targeting && player.alive) onTarget(player.id);
              })}
            >
              {turnLabel && <span className="player-pill__order">{turnLabel}</span>}
              {player.alive && <CoinRing coins={player.coins} />}
              {/* The avatar circle, not the pill, is the coin/card anchor: the
                  seat arc is recomputed in JS every render, so the animation
                  layer measures this node instead of deriving a position. */}
              <span className="player-pill__avatar" ref={anchorRef(seatAnchor(player.id))}>
                {/* A face card wins over the initial; bots never carry one, so the
                    glyph below is still their only avatar. */}
                {player.avatar ? (
                  <img src={player.avatar} alt="" />
                ) : player.bot ? (
                  <Bot size={21} strokeWidth={2.3} />
                ) : (
                  player.name.slice(0, 1).toUpperCase()
                )}
              </span>
              <span className="player-pill__info">
                {/* The avatar already carries the bot glyph, so no BOT tag here. */}
                <span className="player-pill__name">
                  {player.name}
                  {!player.connected && <WifiOff size={11} />}
                </span>
                <span className="player-pill__meta">
                  {/* Below 7 the ring says everything and a numeral is noise.
                      From 7 the exact figure starts changing decisions — can
                      they Coup, can they still Assassinate after — so it comes
                      back, loudly. */}
                  {player.alive && player.coins >= COUP_COST && (
                    <motion.b
                      className={
                        player.coins >= COUP_FORCED
                          ? "coin-count coin-count--forced"
                          : "coin-count"
                      }
                      key={player.coins}
                      initial={{ scale: 1.5 }}
                      animate={{ scale: 1 }}
                      title={
                        player.coins >= COUP_FORCED
                          ? "Must Coup on their turn"
                          : "Can afford a Coup"
                      }
                    >
                      {player.coins}
                    </motion.b>
                  )}
                  <span className="influence-pips">
                    {player.influence.map((card) => (
                      <span
                        key={card.id}
                        className={card.revealed ? "influence-pip influence-pip--lost" : "influence-pip"}
                        title={card.revealed && card.role ? ROLES[card.role].name : "Hidden Influence"}
                        style={
                          card.revealed && card.role
                            ? ({ "--pip-color": ROLES[card.role].color } as React.CSSProperties)
                            : undefined
                        }
                      />
                    ))}
                  </span>
                </span>
              </span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {heldPlayer && (
          <motion.aside
            className="lost-card-peek"
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.98 }}
            transition={{ duration: 0.14 }}
          >
            <header>
              <span>
                <EyeOff size={17} />
                <b>{heldPlayer.name}</b>
              </span>
              <small>REVEALED</small>
            </header>
            <div className="lost-card-peek__cards">
              {heldLostCards.length ? (
                heldLostCards.map((card) => {
                  if (!card.role) return null;
                  const role = ROLES[card.role];
                  return (
                    <span
                      className="lost-card-mini"
                      key={card.id}
                      style={{ "--role-color": role.color } as React.CSSProperties}
                    >
                      <img src={role.image} alt="" />
                      <span>
                        <b>{role.name}</b>
                        <small>LOST</small>
                      </span>
                    </span>
                  );
                })
              ) : (
                <span className="lost-card-peek__empty">No lost cards yet</span>
              )}
            </div>
            <section className="peek-plays">
              <header>
                <b>Recent moves</b>
                <small>Newest first</small>
              </header>
              <div className="peek-plays__list">
                {heldPlays.length ? (
                  heldPlays.map((entry) => {
                    const action = ACTIONS[entry.actionType];
                    const role = action.role ? ROLES[action.role] : null;
                    const targetName = entry.targetId
                      ? players.find((player) => player.id === entry.targetId)?.name
                      : null;
                    return (
                      <div className="peek-plays__row" key={entry.id}>
                        <span
                          className={
                            role ? "peek-plays__art" : "peek-plays__art peek-plays__art--plain"
                          }
                          style={
                            role ? ({ "--role-color": role.color } as React.CSSProperties) : undefined
                          }
                        >
                          {role ? <img src={role.image} alt="" /> : null}
                        </span>
                        <b>{action.name}</b>
                        {targetName && <small>on {targetName}</small>}
                      </div>
                    );
                  })
                ) : (
                  <span className="peek-plays__empty">Nothing declared yet</span>
                )}
              </div>
            </section>
            <footer>Release to close</footer>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
